// Read-only views for the rotary era (merged into selectors.wellView / rigsView; see CONTRACT "M2 rotary sim").
// Pure: never mutates state.
import { eqDef, fieldDef, stat, unitPrice, defsByCat, yf } from './defs.js';
import { strataAt, stratumAt } from './geo.js';
import { mudDef, mudWeight, mudRange, pressureWindow, baritePerFt, mudAvailable } from './mud.js';
import { drillCtx, hydraulicsF, ballingF, rotaryProjection, rpmCurve, RPM_MIN, RPM_MAX, RPM_STOPS, effectiveStrata } from './rotary.js';
import { pipeStats, twistOffChance, inspectCost } from './pipe.js';
import { rigBop, bopFailChance, cappingMult } from './wellcontrol.js';
import { toolpusherFor, hasMudMan } from './automation.js';
import { offsetCount, rivalDrainPctYr, rivalDrainByRes, rule37Fee, skidDays } from './fieldops.js';
import { bestCement } from './cement.js';

const ROTARY_WORK = ['DRILLING', 'PAUSED', 'TRIPPING', 'RUNNING_CASING', 'FISHING', 'CLEANOUT', 'RIGGING_UP', 'STUCK', 'CEMENTING', 'SHUT_IN_KICK', 'KILLING', 'AT_TD'];

export function rotaryWellView(state, well, rig, cur, dailyCost) {
  const isRotary = well.rigType === 'rotary' || !!well.contract;
  const base = { rigType: well.rigType ?? 'cable', bypassed: well.bypassed ?? [], contract: well.contract ?? null, kick: well.kick ?? null,
    blowout: well.blowout ?? null, stuck: well.stuck ?? null, annular: well.annular ?? null, waterCut: well.waterCut ?? 0 };
  if (!isRotary) return base;
  const tripping = well.status === 'TRIPPING';
  const w = pressureWindow(state, well, { tripping, drilling: well.status === 'DRILLING' });
  const zones = w.zones.map((z) => ({ top: z.top, bottom: z.bottom, name: z.name, rock: z.rock, kind: z.kind, pore: round1(z.pore), frac: round1(z.frac),
    open: z.open, cased: z.cased, cemented: z.cemented, permeable: z.permeable, gas: z.gas, lossZone: z.lossZone, known: true }));
  const pressureWindowV = {
    pore: round1(w.pore), frac: round1(w.frac), mw: round1(w.mw), mwEff: round1(w.mwEff), ecd: round2(w.ecd), swab: w.swab, tripping,
    marginLow: round2(w.mw - w.swab - w.pore), marginHigh: round2(w.frac - w.ecd), pressureShoe: w.pressureShoe, depthProfile: zones,
    kickRisk: w.mw - w.swab < w.pore, lossRisk: w.ecd > w.frac,
  };
  const mdef = mudDef(well);
  const ppg = mudWeight(well);
  const mud = mdef ? {
    defId: mdef.id, def: mdef, ppg, auto: !!well.mud?.auto, range: mudRange(mdef), cakeQuality: stat(mdef, 'cakeQuality', 0),
    baritePerFt: baritePerFt(state, ppg), costPerFt: unitPrice(state, mdef) + baritePerFt(state, ppg),
    options: defsByCat('mud').filter((d) => mudAvailable(state, d)).map((d) => ({ id: d.id, name: d.name, range: mudRange(d), pricePerFt: unitPrice(state, d) })),
    autoAvailable: !!rig && (!!toolpusherFor(state, rig) || hasMudMan(state, rig)), mudMan: hasMudMan(state, rig),
  } : null;
  const ctx = drillCtx(state, well);
  const pdef = well.string ? eqDef(well.string.defId) : null;
  const string = well.string ? {
    ...well.string, def: pdef, ratedDepth: pipeStats(pdef).ratedDepth, stockJoints: Math.floor(state.stock[well.string.defId]?.qty ?? 0),
    twistOffRisk: twistOffChance(well, pdef, { rpm: well.rpm ?? 1, h: cur?.hard ?? 3, collarsDef: ctx?.collarsDef }),
  } : null;
  let rpm = null;
  if (ctx?.bit && ROTARY_WORK.includes(well.status) && cur) {
    const now = rotaryProjection(state, well, cur, { wob: well.wob ?? 1, rpm: well.rpm ?? 1 }, dailyCost);
    rpm = { value: well.rpm ?? 1, min: RPM_MIN, max: RPM_MAX, stops: RPM_STOPS, rpmOpt: stat(ctx.bitDef, 'rpmOpt', 1), offOpt: round2(Math.abs((well.rpm ?? 1) - stat(ctx.bitDef, 'rpmOpt', 1))),
      projectedRop: now.rop, costPerFt: now.costPerFt, bitDaysLeft: now.bitDays, risk: now.risk, curve: rpmCurve(state, well, cur, dailyCost) };
  }
  const bop = rig ? rigBop(state, rig) : null;
  const prog = rig?.auto?.program ?? null;
  return {
    ...base, rpm, mud, pressureWindow: pressureWindowV, window: pressureWindowV, string,
    hydF: ctx ? hydraulicsF(ctx, Math.max(1, well.depth)) : null, ballF: ctx && cur ? ballingF(cur, ctx, well.wob ?? 1, mdef) : 1,
    bearing: ctx?.bit && stat(ctx.bitDef, 'bearingDays', null) ? (ctx.bit.bearing ?? 100) : null,
    bop: bop ? { uid: bop.item.uid, defId: bop.def.id, name: bop.def.name, condition: bop.item.condition, failChance: bopFailChance(state, bop) } : null,
    cappingMult: rig ? cappingMult(state, rig) : 1,
    gear: rig ? gearView(state, rig) : null,
    program: prog ? { ...prog, current: prog.sections.find((s) => s.toDepth > well.depth) ?? prog.sections.at(-1), next: well.stopAt ?? null } : null,
    toolpusher: rig ? (toolpusherFor(state, rig)?.name ?? null) : null,
    cement: bestCement(state) ? { defId: bestCement(state).id, name: bestCement(state).name, price: unitPrice(state, bestCement(state)), wocDays: stat(bestCement(state), 'wocDays', 2) } : null,
    effectiveStrata: effectiveStrata(state, well, strataAt(fieldDef(well.fieldId), state.fields[well.fieldId].plots[well.plot])).map((s) => ({ idx: s.idx, stableDays: s.stableDays })),
  };
}

export function rotaryCommands(state, well, rig) {
  const s = well.status;
  const rotary = well.rigType === 'rotary' || !!well.contract;
  const open = ['DRILLING', 'PAUSED', 'AT_TD', 'TRIPPING', 'DRY'].includes(s);
  return {
    ...(well.rigType === 'rotary' ? { dressBit: false } : {}),
    setMud: rotary && !['PLUGGED', 'PRODUCING', 'SHUT_IN', 'DEPLETED'].includes(s),
    setRpm: rotary && !!rig,
    cement: !!bestCement(state) && (well.casing ?? []).some((c) => !c.cemented && !c.cementing) && (['DRILLING', 'PAUSED', 'AT_TD', 'DRY'].includes(s) ? !!rig : ['PRODUCING', 'SHUT_IN', 'DEPLETED'].includes(s)),
    killWell: rotary && open,
    workPipe: s === 'STUCK',
    fish: s === 'STUCK',
    sidetrack: rotary && ['STUCK', 'FISHING'].includes(s),
    recomplete: (well.revealed ?? []).some((id) => id !== well.reservoirId) && ['DRY', 'AT_TD', 'SUSPENDED', 'PRODUCING', 'SHUT_IN', 'DEPLETED'].includes(s),
    testBop: !!rig?.bopUid,
    moveRig: !!rig && !['DRILLING', 'TRIPPING', 'RUNNING_CASING', 'FISHING', 'STUCK', 'CEMENTING', 'SHUT_IN_KICK', 'KILLING', 'BLOWOUT', 'GUSHER', 'COMPLETING', 'RIGGING_UP'].includes(s),
  };
}

export function gearView(state, rig) {
  const one = (uid) => { const it = uid ? state.items[uid] : null; return it ? { uid: it.uid, defId: it.defId, name: eqDef(it.defId)?.name, condition: it.condition } : null; };
  return { pump: one(rig.gear?.pump), collars: one(rig.gear?.collars), capping: one(rig.gear?.capping), bop: one(rig.bopUid) };
}

export function rigM2View(state, rig) {
  const tp = toolpusherFor(state, rig);
  return { gear: gearView(state, rig), bop: gearView(state, rig).bop, toolpusher: tp ? { id: tp.id, name: tp.name, skill: tp.skill } : null,
    program: rig.auto?.program ?? null, skidDays: skidDays(rig) };
}

export function pipeView(state) {
  const out = [];
  for (const def of defsByCat('pipe')) {
    const st = state.stock[def.id];
    const inUse = Object.values(state.wells).filter((w) => w.string?.defId === def.id).map((w) => ({ wellId: w.id, name: w.name, joints: w.string.joints, fatigue: round1(w.string.fatigue) }));
    if (!st && !inUse.length) continue;
    out.push({ defId: def.id, def, joints: Math.floor(st?.qty ?? 0), fatigue: round1(st?.wear ?? 0), inUse, inspectCost: inspectCost(state, def.id), ...pipeStats(def) });
  }
  return out;
}

export function fieldRigsView(state, fieldId) {
  const rigs = Object.values(state.rigs).filter((r) => r.at?.fieldId === fieldId).map((r) => {
    const w = r.wellId ? state.wells[r.wellId] : null;
    return { uid: r.uid, name: r.name, defId: r.defId, plot: r.at.plot, well: w ? { id: w.id, name: w.name, status: w.status, depth: w.depth } : null, skidDays: skidDays(r) };
  });
  const wells = Object.values(state.wells).filter((w) => w.fieldId === fieldId).map((w) => ({ id: w.id, name: w.name, plot: w.plot, status: w.status, depth: w.depth, rate: w.prod?.rate ?? 0, contract: !!w.contract }));
  const f = state.fields[fieldId];
  return {
    rigs, wells, offsetWells: offsetCount(state, fieldId), rivalDrainPctYr: rivalDrainByRes(state, fieldId), rivalDrainMaxPctYr: rivalDrainPctYr(state, fieldId), townLot: fieldDef(fieldId)?.townLot ?? null,
    rule37: f ? f.plots.map((p, i) => (p.owned && !p.wellId ? rule37Fee(state, fieldId, i) : 0)) : [],
    year: yf(state),
  };
}

// WOB curve for rotary wells (rotary ROP / tooth+bearing wear, at the current RPM).
export function rotaryWobCurve(state, well, cur, dailyCost) {
  const rpm = well.rpm ?? 1;
  const now = rotaryProjection(state, well, cur, { wob: well.wob ?? 1, rpm }, dailyCost);
  const costCurve = [];
  for (let w = 0.5; w <= 1.6 + 1e-9; w += 0.1) {
    const p = rotaryProjection(state, well, cur, { wob: Math.round(w * 10) / 10, rpm }, dailyCost);
    costCurve.push({ wob: p.wob, rop: p.rop, costPerFt: p.costPerFt, risk: p.risk });
  }
  // WOB × RPM cost grid for the two-slider readout (null = can't drill there)
  const wobs = [0.6, 0.7, 0.8, 0.9, 1.0, 1.1, 1.2, 1.3, 1.4, 1.5, 1.6], rpms = [0.6, 0.7, 0.8, 0.9, 1.0, 1.1, 1.2, 1.3, 1.4, 1.5, 1.6];
  let best = null;
  const cost = wobs.map((wb) => rpms.map((rp) => {
    const p = rotaryProjection(state, well, cur, { wob: wb, rpm: rp }, dailyCost);
    if (p.costPerFt != null && (!best || p.costPerFt < best.costPerFt)) best = { wob: wb, rpm: rp, costPerFt: p.costPerFt, rop: p.rop };
    return p.costPerFt == null ? null : Math.round(p.costPerFt * 1000) / 1000;
  }));
  const remaining = Math.max(0, well.target - well.depth);
  const cells = [];
  wobs.forEach((wb, i) => rpms.forEach((rp, j) => cells.push({ wob: wb, rpm: rp, costPerFt: cost[i][j] })));
  return { costGrid: { wob: wobs, rpm: rpms, cost, best, cells }, projectedRop: now.rop, bitDaysLeft: now.bitDays, costPerFt: now.costPerFt, daysToTarget: now.rop > 0 ? remaining / now.rop : null, riskMult: now.risk,
    riskLevel: now.risk < 1.05 ? 'low' : now.risk < 1.6 ? 'medium' : now.risk < 2.2 ? 'high' : 'extreme', costCurve };
}

const round1 = (x) => Math.round(x * 10) / 10;
const round2 = (x) => Math.round(x * 100) / 100;
export const _stratumAt = stratumAt;
